import { useEffect } from 'react';
import { SITE_URL, SeoEntry } from '../data/seo';

const ensureMeta = (selector: string, create: () => HTMLMetaElement) => {
  const existing = document.head.querySelector<HTMLMetaElement>(selector);
  if (existing) return existing;

  const meta = create();
  document.head.appendChild(meta);
  return meta;
};

const ensureLink = (selector: string, create: () => HTMLLinkElement) => {
  const existing = document.head.querySelector<HTMLLinkElement>(selector);
  if (existing) return existing;

  const link = create();
  document.head.appendChild(link);
  return link;
};

const removeHeadElement = (selector: string) => {
  document.head.querySelector(selector)?.remove();
};

const toAbsoluteImageUrl = (image?: string) => {
  if (!image) return '';
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  if (image.startsWith('//')) return `https:${image}`;
  if (image.startsWith('/uploads/')) return `${SITE_URL}/api${image}`;
  return `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
};

const buildSchema = (seo: SeoEntry) => {
  const url = `${SITE_URL}${seo.canonicalPath}`;
  const image = toAbsoluteImageUrl(seo.image);

  if (seo.type === 'Article') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: seo.title,
      description: seo.description,
      url,
      mainEntityOfPage: url,
      ...(image ? { image } : {}),
      author: {
        '@type': 'Person',
        name: 'Enzo Chevallier',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Virtuoz Solutions',
      },
      keywords: seo.tags?.join(', '),
    };
  }

  if (seo.type === 'SoftwareApplication') {
    return {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: seo.title.split('|')[0].trim(),
      description: seo.description,
      url,
      ...(image ? { image } : {}),
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      creator: {
        '@type': 'Organization',
        name: 'Virtuoz Solutions',
      },
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: seo.siteName || 'Virtuoz Solutions',
    description: seo.description,
    url,
  };
};

export const usePageSeo = (seo: SeoEntry) => {
  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath}`;
    const imageUrl = toAbsoluteImageUrl(seo.image);

    document.title = seo.title;

    ensureMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    }).setAttribute('content', seo.description);

    ensureMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    }).setAttribute('content', seo.title);

    ensureMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    }).setAttribute('content', seo.description);

    ensureMeta('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    }).setAttribute('content', canonicalUrl);

    ensureMeta('meta[property="og:type"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      return meta;
    }).setAttribute('content', seo.type === 'Article' ? 'article' : 'website');

    if (imageUrl) {
      ensureMeta('meta[property="og:image"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image');
        return meta;
      }).setAttribute('content', imageUrl);

      ensureMeta('meta[property="og:image:secure_url"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image:secure_url');
        return meta;
      }).setAttribute('content', imageUrl);

      ensureMeta('meta[property="og:image:alt"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image:alt');
        return meta;
      }).setAttribute('content', seo.title);

      ensureMeta('meta[property="og:image:width"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image:width');
        return meta;
      }).setAttribute('content', '1536');

      ensureMeta('meta[property="og:image:height"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:image:height');
        return meta;
      }).setAttribute('content', '1024');
    } else {
      removeHeadElement('meta[property="og:image"]');
      removeHeadElement('meta[property="og:image:secure_url"]');
      removeHeadElement('meta[property="og:image:alt"]');
      removeHeadElement('meta[property="og:image:width"]');
      removeHeadElement('meta[property="og:image:height"]');
    }

    ensureMeta('meta[name="twitter:card"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:card');
      return meta;
    }).setAttribute('content', 'summary_large_image');

    ensureMeta('meta[name="twitter:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:title');
      return meta;
    }).setAttribute('content', seo.title);

    ensureMeta('meta[name="twitter:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:description');
      return meta;
    }).setAttribute('content', seo.description);

    if (imageUrl) {
      ensureMeta('meta[name="twitter:image"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:image');
        return meta;
      }).setAttribute('content', imageUrl);

      ensureMeta('meta[name="twitter:image:alt"]', () => {
        const meta = document.createElement('meta');
        meta.setAttribute('name', 'twitter:image:alt');
        return meta;
      }).setAttribute('content', seo.title);
    } else {
      removeHeadElement('meta[name="twitter:image"]');
      removeHeadElement('meta[name="twitter:image:alt"]');
    }

    ensureLink('link[rel="canonical"]', () => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      return link;
    }).setAttribute('href', canonicalUrl);

    let schema = document.head.querySelector<HTMLScriptElement>('script[data-seo-schema="true"]');
    if (!schema) {
      schema = document.createElement('script');
      schema.type = 'application/ld+json';
      schema.dataset.seoSchema = 'true';
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify(buildSchema(seo));
  }, [seo]);
};
